// src/components/Accounts/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  IconButton
} from '@mui/material';
import { Refresh, ExitToApp, Delete, PersonRemove } from '@mui/icons-material';
import { getCurrentUser, getAllUsers, logout, UserInfo } from '../../services/authService';
import axios from 'axios';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const checkAdminAndFetchUsers = async () => {
      try {
        // Check if user is logged in and is an admin
        const currentUser = getCurrentUser();

        if (!currentUser) {
          navigate('/login');
          return;
        }

        if (currentUser.role !== 'admin') {
          navigate('/user');
          return;
        }

        await fetchUsers();
      } catch (err) {
        console.error('Error checking admin permissions:', err);
        navigate('/login');
      }
    };

    checkAdminAndFetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const allUsers = await getAllUsers();
      setUsers(allUsers);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openDeleteDialog = (user: UserInfo) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const removeUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      const currentUser = getCurrentUser();

      if (currentUser?._id === userToDelete._id) {
        setError("You cannot delete your own account");
        closeDeleteDialog();
        return;
      }

      // Set auth header with current user's token
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentUser?.token}`;

      // Make API call to delete user
      await axios.delete(`/api/users/${userToDelete._id}`);

      // Update local state to remove the deleted user
      setUsers(users.filter(user => user._id !== userToDelete._id));
      setSuccess(`User ${userToDelete.username} has been removed successfully`);

      // Close the dialog
      closeDeleteDialog();
    } catch (err: any) {
      console.error('Error removing user:', err);
      setError(err.response?.data?.message || 'Failed to remove user. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Admin Dashboard</Typography>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={fetchUsers}
              startIcon={<Refresh />}
              sx={{ mr: 2 }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleLogout}
              startIcon={<ExitToApp />}
            >
              Logout
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Typography variant="h6" gutterBottom>
          User Management
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>{user._id}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={user.role === 'admin' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => openDeleteDialog(user)}
                          disabled={getCurrentUser()?._id === user._id}
                          title={getCurrentUser()?._id === user._id ? "Cannot delete your own account" : "Delete user"}
                        >
                          <PersonRemove />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm User Removal</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to remove user <strong>{userToDelete?.username}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary" disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={removeUser}
            color="error"
            variant="contained"
            startIcon={<Delete />}
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Removing...' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AdminDashboard;
